import { FC, useMemo } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { SelectCellRenderer } from '@aleph/supergrid';
import { useGeoLabels, extractGeoIds, GeoOption } from 'wb-spotify/app/routes/superGrid/hooks/useGeoLabels';
import { GeoType } from 'wb-spotify/graphql/generated';

interface IGeoSelectCellRendererParams extends ICellRendererParams {
	geoType: GeoType;
	fieldName: 'cityIds' | 'dmaIds' | 'regionIds' | 'postalCodeIds';
}

/**
 * Check if the value is already an array of objects with labels (not just IDs)
 */
function hasLabels(value: any[] | undefined): boolean {
	if (!value || value.length === 0) {
		return false;
	}

	// Check if first item is an object with a label property
	const firstItem = value[0];

	return (
		typeof firstItem === 'object'
		&& firstItem !== null
		&& 'label' in firstItem
		&& typeof firstItem.label === 'string'
		&& firstItem.label !== firstItem.value // Label is different from value (not just ID)
	);
}

/**
 * Cell renderer for geo fields that fetches labels from IDs
 * Only fetches labels on first render when data contains only IDs (from backend)
 * If data already has labels (from user selection), uses them directly
 */
const GeoSelectCellRenderer: FC<IGeoSelectCellRendererParams> = (params) => {
	const rawValue = params.data?.groupTargets?.groupTargetsGeoTargets?.[params.fieldName];
	const hasLabelsAlready = hasLabels(rawValue);

	// Only fetch labels if we have IDs without labels (first render from backend)
	const geoIds = hasLabelsAlready ? undefined : extractGeoIds(params, params.fieldName);
	const { geoOptions, loading } = useGeoLabels(geoIds, params.geoType);

	// Determine which value to use: fetched options or existing value with labels
	const displayValue = useMemo(() => {
		if (hasLabelsAlready) {
			// Data already has labels, use it directly
			return rawValue;
		}

		// Use fetched options
		return geoOptions;
	}, [hasLabelsAlready, rawValue, geoOptions]);

	// Merge the display value with the cell renderer params
	const mergedParams = useMemo(() => ({
		...params,
		value:              displayValue, // Use either existing value with labels or fetched options
		cellRendererParams: {
			// @ts-ignore
			...params.cellRendererParams,
			labelGetter: (item: GeoOption) => item?.label || item?.value || '',
		},
	}), [params, displayValue]);

	// If loading and no options yet, show loading state
	if (!hasLabelsAlready && loading && (!geoOptions || geoOptions.length === 0)) {
		return <span className="text-gray-400">Loading...</span>;
	}

	// Use SelectCellRenderer with the display value
	return <SelectCellRenderer {...mergedParams} />;
};

export default GeoSelectCellRenderer;
